import React, { useCallback, useEffect, useRef, useState } from 'react';
import './SequencePreviewPanel.css';

export const PREVIEW_WIDTH_KEY = 'brainstormy.sequencePreviewWidth';
export const PREVIEW_DEFAULT_WIDTH = 340;
export const PREVIEW_MIN_WIDTH = 280;
export const PREVIEW_ABS_MAX = 720;
export const PREVIEW_KEYBOARD_STEP = 24;
export const CHAT_KEEP_WIDTH = 320;
export const RESIZE_LABEL = 'Resize sequence preview';

export function previewWidthMax(containerWidth = 0) {
  if (!(Number(containerWidth) > 0)) return PREVIEW_ABS_MAX;
  const share = Math.floor(Number(containerWidth) * 0.55);
  const leaveChat = Math.floor(Number(containerWidth) - CHAT_KEEP_WIDTH);
  const capped = Math.min(PREVIEW_ABS_MAX, share, leaveChat);
  return Math.max(PREVIEW_MIN_WIDTH, capped);
}

export function clampPreviewWidth(width, containerWidth = 0) {
  const max = previewWidthMax(containerWidth);
  const desired = Number(width);
  const base = Number.isFinite(desired) ? desired : max;
  return Math.round(Math.min(max, Math.max(PREVIEW_MIN_WIDTH, base)));
}

function storedWidth() {
  try {
    const raw = window.localStorage.getItem(PREVIEW_WIDTH_KEY);
    if (raw == null || raw === '') return PREVIEW_DEFAULT_WIDTH;
    return clampPreviewWidth(Number(raw), 0);
  } catch {
    return PREVIEW_DEFAULT_WIDTH;
  }
}

export default function PlotFocusColumn({ children }) {
  const columnRef = useRef(null);
  const startRef = useRef({ x: 0, width: PREVIEW_DEFAULT_WIDTH });
  const [width, setWidth] = useState(storedWidth);
  const [containerWidth, setContainerWidth] = useState(0);
  const [resizing, setResizing] = useState(false);
  const maxWidth = previewWidthMax(containerWidth);

  const measure = useCallback(() => {
    const parent = columnRef.current?.parentElement;
    setContainerWidth(parent ? parent.getBoundingClientRect().width || 0 : 0);
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  useEffect(() => {
    setWidth((current) => clampPreviewWidth(current, containerWidth));
  }, [containerWidth]);

  useEffect(() => {
    try {
      window.localStorage.setItem(PREVIEW_WIDTH_KEY, String(width));
    } catch {
      // Private browsing can reject the write. The width still holds for this view.
    }
  }, [width]);

  useEffect(() => {
    if (!resizing) return undefined;
    const onMove = (event) => {
      const parent = columnRef.current?.parentElement;
      const available = parent ? parent.getBoundingClientRect().width : 0;
      const next = startRef.current.width + (startRef.current.x - event.clientX);
      setWidth(clampPreviewWidth(next, available));
    };
    const onUp = () => setResizing(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    const previousCursor = document.body.style.cursor;
    const previousSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousSelect;
    };
  }, [resizing]);

  const onResizeStart = (event) => {
    if (event.button != null && event.button !== 0) return;
    event.preventDefault();
    startRef.current = { x: event.clientX, width };
    setResizing(true);
  };

  const onResizeKey = (event) => {
    const available = columnRef.current?.parentElement
      ? columnRef.current.parentElement.getBoundingClientRect().width
      : containerWidth;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setWidth((current) => clampPreviewWidth(current + PREVIEW_KEYBOARD_STEP, available));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      setWidth((current) => clampPreviewWidth(current - PREVIEW_KEYBOARD_STEP, available));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setWidth(PREVIEW_MIN_WIDTH);
    } else if (event.key === 'End') {
      event.preventDefault();
      setWidth(clampPreviewWidth(Number.POSITIVE_INFINITY, available));
    }
  };

  return (
    <aside
      ref={columnRef}
      className="chat-interface__plot-side"
      aria-label="Sequence preview column"
      style={{ width: `${width}px`, flexBasis: `${width}px` }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={RESIZE_LABEL}
        aria-valuemin={PREVIEW_MIN_WIDTH}
        aria-valuemax={maxWidth}
        aria-valuenow={width}
        tabIndex={0}
        className={`seq-preview-resize${resizing ? ' seq-preview-resize--active' : ''}`}
        onMouseDown={onResizeStart}
        onKeyDown={onResizeKey}
        onDoubleClick={() => setWidth(clampPreviewWidth(PREVIEW_DEFAULT_WIDTH, containerWidth))}
      >
        <span className="seq-preview-resize__dots" aria-hidden="true">
          <span className="seq-preview-resize__dot" />
          <span className="seq-preview-resize__dot" />
          <span className="seq-preview-resize__dot" />
        </span>
      </div>
      {children}
    </aside>
  );
}
